// src/components/heatmaps/HistoryView.jsx

import { useState, useMemo, useRef, useCallback, useContext } from "react";
import React from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import styles from "../../css/heatmaps/HistoryView.module.css";
import Modal from "../Modal";
import HeatmapPreviewModal from "./HeatmapPreviewModal";
// i18n-aware formatter kept under an alias to avoid name clash with local helper
import { formatDate as formatDateI18n } from "../../utils/functions/formatDate";
import { useParams } from "react-router-dom";
import { ReactSVG } from "react-svg";
import wandIcon from "../../img/icons/wand.svg";
import ModalLoading from "../ModalLoading";
import axiosInstance from "../../api/AxiosConfig";
import { BsStars } from "react-icons/bs";
import AIPurposeModal from "./AIPurposeModal";
import { getAiTypeDescription } from "./aiPurposeOptions";
import { AppContext } from "../../context/AppContext";

const HistoryView = ({
  historyData,
  isLoading,
  progressData,
  onAiAnalysisCompleted,
}) => {
  const { userData, setUserData, setShowModalCredit } = useContext(AppContext);
  const { lng } = useParams();
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sortBy, setSortBy] = useState("date_desc");
  const [dateRange, setDateRange] = useState([null, null]);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Progress/loader states
  const [displayPercent, setDisplayPercent] = useState(0);
  const [showLoading, setShowLoading] = useState(!!isLoading);
  const completionRef = useRef(false);

  // Refs pro search
  const searchTimeoutRef = useRef(null);
  // Ref pro zjištění kliknutí mimo oblast s datovým rozsahem a kalendářem
  const dateRangeRef = useRef(null);

  const handleCloseModal = () => {
    setSelectedImage(null);
    setSelectedAnalysis("");
    setSelectedItem(null);
  };

  // ---- JSON analysis helpers (aligned with AnalysisViewer) ----
  const extractPayload = (data) => {
    if (data == null) return "";
    if (typeof data === "string") {
      try {
        let t = data.trim();
        if (t.startsWith("```")) {
          t = t.replace(/^```(?:json|javascript)?\n?/i, "");
          t = t.replace(/```\s*$/i, "");
          t = t.trim();
        }
        if (
          (t.startsWith("{") && t.endsWith("}")) ||
          (t.startsWith("[") && t.endsWith("]"))
        ) {
          return JSON.parse(t);
        }
      } catch (e) {
        // ignore, keep as string
      }
      return data;
    }
    if (Array.isArray(data)) return data;
    if (typeof data === "object") {
      // Preserve explicit error objects so the UI can render a friendly message
      if (data._error === true || data.error === true) return data;
      if (
        data.typ ||
        data.analyza ||
        data.doporuceni ||
        data.hodnoceni ||
        data.permoniq_doporuceni
      )
        return data;
      const candidate =
        data.analysis_result ||
        data.message ||
        data.result ||
        data.detail ||
        data.analysis ||
        data.output ||
        "";
      return extractPayload(candidate);
    }
    return "";
  };

  const renderAnalysisContent = (payload) => {
    if (!payload) return null;
    // Friendly error message with a clickable e‑mail when backend request fails
    if (
      (typeof payload === "object" &&
        (payload._error === true || payload.error === true)) ||
      (typeof payload === "string" &&
        /(error|chyba|failed|status\s*code|4\d{2}|5\d{2})/i.test(payload))
    ) {
      const email =
        (typeof payload === "object" && payload.email) || "info@permoniq.com";
      return (
        <div className={styles.aiBlock}>
          <p>
            Při pokusu o AI analýzu došlo k chybě. Zkuste to prosím znovu
            později.
          </p>
          <p>
            Pokud se vám během tohoto pokusu odečetl kredit, napište nám na{" "}
            <a
              className="link"
              href={`mailto:${email}`}
              rel="noopener noreferrer"
              style={{ color: "inherit" }}
            >
              {email}
            </a>
            .
          </p>
        </div>
      );
    }
    const renderList = (pairs) => (
      <ul className={styles.aiList}>
        {pairs.map(([label, value], i) =>
          value ? (
            <li key={i}>
              <strong>{label}:</strong> {value}
            </li>
          ) : null
        )}
      </ul>
    );
    const Block = ({ obj, idx }) => {
      if (!obj || typeof obj !== "object") return null;
      const analysisPairs = [];
      if (obj.analyza && typeof obj.analyza === "object") {
        const a = obj.analyza;
        analysisPairs.push(["Logo", a.logo]);
        analysisPairs.push(["Claim", a.claim]);
        analysisPairs.push(["CTA", a.cta]);
        analysisPairs.push(["Podklad", a.podklad]);
      }
      const recPairs = [];
      if (obj.doporuceni && typeof obj.doporuceni === "object") {
        const d = obj.doporuceni;
        recPairs.push(["Logo", d.logo]);
        recPairs.push(["Claim", d.claim]);
        recPairs.push(["CTA", d.cta]);
        recPairs.push(["Podklad", d.podklad]);
      }
      return (
        <div className={styles.aiBlock} key={idx}>
          {obj.typ ? (
            <h2 className={styles.aiBlockTitle}>
              {getAiTypeDescription(obj.typ) || String(obj.typ)}
            </h2>
          ) : null}
          {analysisPairs.length > 0 && (
            <section className={styles.aiSection}>
              <h3 className={styles.aiSectionTitle}>Analýza</h3>
              {renderList(analysisPairs)}
            </section>
          )}
          {recPairs.length > 0 && (
            <section className={styles.aiSection}>
              <h3 className={styles.aiSectionTitle}>Doporučení</h3>
              {renderList(recPairs)}
            </section>
          )}
          {obj.hodnoceni ? (
            <p>
              <strong>Celkové hodnocení:</strong> {String(obj.hodnoceni)}
            </p>
          ) : null}
          {obj.permoniq_doporuceni ? (
            <section className={styles.aiSection}>
              <h3 className={styles.aiSectionTitle}>Doporučení PermoniQ</h3>
              <p style={{ whiteSpace: "pre-wrap" }}>
                {String(obj.permoniq_doporuceni)}
              </p>
            </section>
          ) : null}
        </div>
      );
    };
    if (Array.isArray(payload)) {
      return payload.map((o, i) => <Block obj={o} idx={i} key={i} />);
    }
    if (typeof payload === "object") {
      return <Block obj={payload} />;
    }
    return <p style={{ whiteSpace: "pre-wrap" }}>{String(payload)}</p>;
  };

  // Validate payload has expected JSON structure (object/array with known keys)
  const isValidAnalysisPayload = (payload) => {
    if (!payload) return false;
    const isValidObj = (o) =>
      o &&
      typeof o === "object" &&
      (o.typ ||
        o.analyza ||
        o.doporuceni ||
        o.hodnoceni ||
        o.permoniq_doporuceni);
    if (Array.isArray(payload)) return payload.some((o) => isValidObj(o));
    return isValidObj(payload);
  };

  // Parse overall score (val/max) from JSON or string, tolerant to formats
  const parseScore = (input) => {
    if (!input) return { scoreVal: null, scoreMax: null };
    if (Array.isArray(input)) {
      for (const it of input) {
        const r = parseScore(it);
        if (typeof r.scoreVal === "number" && typeof r.scoreMax === "number")
          return r;
      }
      return { scoreVal: null, scoreMax: null };
    }
    if (typeof input === "object") {
      const raw = input.hodnoceni || input.score || input.scores || null;
      if (typeof raw === "number" && isFinite(raw)) {
        const val = Math.max(0, Math.min(999, Math.trunc(raw)));
        return { scoreVal: val, scoreMax: 10 };
      }
      if (typeof raw === "string") {
        // Prefer explicit "Celkové hodnocení: X[/Y]?"
        let m = raw.match(
          /Celkové\s+hodnocení[^:]*:\s*(?:\*\*|__)?\s*(\d{1,3})(?:\s*[\/⁄]\s*(\d{1,3}))?/i
        );
        if (m) {
          const val = parseInt(m[1], 10);
          const max = m[2] ? parseInt(m[2], 10) : 10;
          if (!isNaN(val) && !isNaN(max) && max > 0)
            return { scoreVal: val, scoreMax: max };
        }
        // Generic X/Y anywhere
        m = raw.match(/\b(\d{1,3})\s*[\/⁄]\s*(\d{1,3})\b/);
        if (m) {
          const val = parseInt(m[1], 10);
          const max = parseInt(m[2], 10) || 10;
          if (!isNaN(val) && !isNaN(max) && max > 0)
            return { scoreVal: val, scoreMax: max };
        }
        // Single number only -> assume /10
        m = raw.match(/^\s*(\d{1,3})\s*$/);
        if (m) {
          const val = parseInt(m[1], 10);
          if (!isNaN(val)) return { scoreVal: val, scoreMax: 10 };
        }
      }
      if (typeof input.analysis_result === "string")
        return parseScore(input.analysis_result);
      return { scoreVal: null, scoreMax: null };
    }
    if (typeof input === "string") {
      // Prefer explicit line with optional /Y
      let m =
        input.match(
          /Celkové\s+hodnocení[^:]*:\s*(?:\*\*|__)?\s*(\d{1,3})(?:\s*[\/⁄]\s*(\d{1,3}))?/i
        ) || input.match(/\b(\d{1,3})\s*[\/⁄]\s*(\d{1,3})\b/);
      if (m) {
        const val = parseInt(m[1], 10);
        const max = m[2] ? parseInt(m[2], 10) : 10;
        if (!isNaN(val) && !isNaN(max) && max > 0)
          return { scoreVal: val, scoreMax: max };
      }
      // Pure numeric string -> /10
      m = input.match(/^\s*(\d{1,3})\s*$/);
      if (m) {
        const val = parseInt(m[1], 10);
        if (!isNaN(val)) return { scoreVal: val, scoreMax: 10 };
      }
    }
    return { scoreVal: null, scoreMax: null };
  };

  const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

  const requestAnalysisWithRetry = async (
    analysisId,
    prompt,
    maxAttempts = 10
  ) => {
    let lastRaw = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const resp = await axiosInstance.post(
          `analyze_heatmaps/${encodeURIComponent(analysisId)}/`,
          
          { user_prompt: prompt }
        );
        const raw = extractPayload(resp?.data);
        lastRaw = raw ?? "";
        if (isValidAnalysisPayload(raw)) {
          return { ok: true, payload: raw };
        }
      } catch (e) {
        lastRaw =
          e?.response?.data?.message ||
          e?.message ||
          "Nepodařilo se analyzovat.";
      }
      await sleep(Math.min(300 * attempt, 1500));
    }
    return { ok: false, payload: lastRaw };
  };

  // --- Analyze flow for historical items ---
  const [isMagicOpen, setIsMagicOpen] = useState(false);
  const [magicType, setMagicType] = useState("Brand");
  const [isMagicSubmitting, setIsMagicSubmitting] = useState(false);

  const PROMPT_MAP = {
    Brand:
      "Banner by měl posílit povědomí o značce a vizuálně prezentovat brand (brandový účel).",
    Akviziční: "Banner by měl zaujmout a přilákat zákazníka (akviziční účel).",
    Konverzní:
      "Banner by měl motivovat uživatele k provedení konkrétní akce, například k nákupu, registraci nebo odeslání formuláře (konverzní účel).",
    "Služba/nástroj":
      "Banner by měl prezentovat konkrétní službu nebo nástroj a povzbudit uživatele k jeho vyzkoušení nebo použití (účel: služba/nástroj).",
  };

  const extractMessage = (data) => {
    if (!data) return "";
    if (typeof data === "string") return data;
    return (
      data.analysis_result ||
      data.message ||
      data.result ||
      data.detail ||
      data.analysis ||
      data.output ||
      ""
    );
  };

  const parseScoreAndStrip = (markdown) => {
    if (!markdown || typeof markdown !== "string") {
      return { stripped: markdown, scoreVal: null, scoreMax: null };
    }
    const lineRegex =
      /.*Celkové\s+hodnocení[^:]*:\s*(?:\*\*|__)?\s*(\d{1,3})(?:\s*[\/⁄]\s*(\d{1,3}))?.*/i;
    const lines = markdown.split(/\r?\n/);
    let scoreVal = null;
    let scoreMax = null;
    const filtered = lines.filter((line) => {
      const m = line.match(lineRegex);
      if (m) {
        const val = parseInt(m[1], 10);
        const max = m[2] ? parseInt(m[2], 10) : 10;
        if (!isNaN(val) && !isNaN(max) && max > 0) {
          scoreVal = val;
          scoreMax = max;
        }
        return false; // odstraň řádek se skóre
      }
      return true;
    });
    const stripped = filtered.join("\n").replace(/\n{3,}/g, "\n\n");
    return { stripped, scoreVal, scoreMax };
  };

  // Toggle kalendáře
  const toggleCalendar = () => {
    setIsCalendarOpen(!isCalendarOpen);
  };

  // Kalendář onChange handler
  const onCalendarChange = (value) => {
    setDateRange(value);
    setIsCalendarOpen(false);
  };

  // Debounced search handler
  const handleSearchChange = useCallback((value) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(value);
    }, 300);
  }, []);

  // Cleanup timeout on unmount
  const cleanupTimeout = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);

  // Cleanup effect
  React.useEffect(() => {
    return cleanupTimeout;
  }, [cleanupTimeout]);

  // Zavření kalendáře při kliknutí mimo nebo při stisku Escape
  React.useEffect(() => {
    if (!isCalendarOpen) return;

    const handlePointerDown = (e) => {
      if (!dateRangeRef.current) return;
      if (!dateRangeRef.current.contains(e.target)) {
        setIsCalendarOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsCalendarOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, {
      passive: true,
    });
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isCalendarOpen]);

  // Keep loader visible until we visually complete to 100% and then switch
  React.useEffect(() => {
    if (isLoading) {
      // New loading cycle started
      completionRef.current = false;
      setShowLoading(true);
      // Initialize progress from incoming value or reset to 0
      const p = Math.max(0, Math.min(progressData?.percent ?? 0, 99));
      setDisplayPercent((prev) => (prev > p ? p : prev));
    } else {
      // Data ready -> finish animation to 100%
      completionRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Animate displayed progress towards target percent
  React.useEffect(() => {
    const target = completionRef.current
      ? 100
      : Math.max(0, Math.min(progressData?.percent ?? 0, 99));

    if (displayPercent === target) return;

    const interval = setInterval(() => {
      setDisplayPercent((p) => {
        if (p >= target) return target;
        // Ease out: bigger steps when far, smaller when close
        const delta = target - p;
        const step = Math.max(1, Math.ceil(delta / 8));
        return Math.min(target, p + step);
      });
    }, 30);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressData?.percent, completionRef.current, displayPercent]);

  // When we hit 100% during completion, hold briefly then hide loader
  React.useEffect(() => {
    if (!completionRef.current) return;
    if (displayPercent < 100) return;
    const t = setTimeout(() => setShowLoading(false), 300);
    return () => clearTimeout(t);
  }, [displayPercent]);

  const filteredAndSortedData = useMemo(() => {
    try {
      let processedData = [...historyData];

      // Filtrování podle date range
      if (dateRange[0] && dateRange[1]) {
        const [startDate, endDate] = dateRange;
        processedData = processedData.filter((item) => {
          if (!item.created_at) return false;
          const itemDate = new Date(item.created_at);
          return itemDate >= startDate && itemDate <= endDate;
        });
      }

      // Filtrování podle search term
      if (searchTerm) {
        processedData = processedData.filter(
          (item) =>
            item.name &&
            item.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Řazení
      switch (sortBy) {
        case "date_asc":
          processedData.sort(
            (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)
          );
          break;
        case "name_asc":
          processedData.sort((a, b) =>
            (a.name || "").localeCompare(b.name || "")
          );
          break;
        case "name_desc":
          processedData.sort((a, b) =>
            (b.name || "").localeCompare(a.name || "")
          );
          break;
        case "date_desc":
        default:
          processedData.sort(
            (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
          );
          break;
      }
      return processedData;
    } catch (error) {
      console.error("Chyba při filtrování a řazení dat:", error);
      return [];
    }
  }, [historyData, searchTerm, sortBy, dateRange]);

  // Local, simple formatter for displaying dates in the list
  const formatDateLocal = (dateString) => {
    const options = { day: "numeric", month: "numeric", year: "numeric" };
    return new Date(dateString).toLocaleDateString("cs-CZ", options);
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setSearchInput("");
    setSortBy("date_desc");
    setDateRange([null, null]);
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (!+bytes) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  };

  // Funkce pro výpočet gradient barvy na základě procent
  const getProgressColor = (percent) => {
    // Barvy aplikace: žlutá -> zelená
    const startColor = [255, 218, 68]; // #ffda44
    const endColor = [34, 224, 108]; // #22e06c

    const ratio = Math.min(percent / 100, 1);
    const r = Math.round(startColor[0] + (endColor[0] - startColor[0]) * ratio);
    const g = Math.round(startColor[1] + (endColor[1] - startColor[1]) * ratio);
    const b = Math.round(startColor[2] + (endColor[2] - startColor[2]) * ratio);

    return `rgb(${r}, ${g}, ${b})`;
  };

  if (showLoading) {
    return (
      <div className={styles.placeholder}>
        <p>Načítám historii...</p>
        <div className={styles.progressContainer}>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{
                width: `${displayPercent}%`,
                backgroundColor: getProgressColor(displayPercent),
              }}
            />
            <span className={styles.progressText}>
              {Math.round(displayPercent)}%
            </span>
          </div>
        </div>
        <p className={styles.progressDetails}>
          {progressData?.total > 0 ? (
            <>
              {formatBytes(progressData?.loaded ?? 0)} /{" "}
              {formatBytes(progressData?.total ?? 0)}
            </>
          ) : (
            <span style={{ opacity: 0 }}>Připravuji stahování...</span>
          )}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className={styles.controlsHeader}>
        <div className={styles.filtersRowInline}>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => {
              const v = e.target.value;
              setSearchInput(v);
              handleSearchChange(v);
            }}
            placeholder="Vyhledat..."
            className={styles.searchInput}
            aria-label="Vyhledat podle názvu"
          />

          <select
            className={styles.sortSelect}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            aria-label="Seřadit heatmapy podle"
          >
            <option value="date_desc">Nejnovější</option>
            <option value="date_asc">Nejstarší</option>
            <option value="name_asc">Název (A-Z)</option>
            <option value="name_desc">Název (Z-A)</option>
          </select>

          <div className={styles.dateInputsGroup} ref={dateRangeRef}>
            <div
              className={styles.dateInputWrap}
              onClick={toggleCalendar}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                (e.key === "Enter" || e.key === " ") && toggleCalendar()
              }
            >
              <input
                type="text"
                readOnly
                value={`${
                  dateRange[0]
                    ? formatDateI18n(lng, dateRange[0])
                    : "dd.mm.rrrr"
                } - ${
                  dateRange[1]
                    ? formatDateI18n(lng, dateRange[1])
                    : "dd.mm.rrrr"
                }`}
                className={styles.dateInput}
                aria-label="Rozsah dat"
              />
              <span className={styles.calendarIcon} aria-hidden="true" />
            </div>

            {isCalendarOpen && (
              <div className={styles.calendarContainerInline}>
                <Calendar
                  onChange={onCalendarChange}
                  selectRange
                  value={dateRange}
                />
              </div>
            )}
          </div>

          <button
            type="button"
            className={`${styles.clearLink} link`}
            onClick={handleResetFilters}
            aria-label="Smazat filtr"
          >
            Smazat&nbsp;filtr
          </button>
        </div>
      </div>

      <div className={styles.historyGrid}>
        <div className={styles.countRow}>
          Nalezeno: {filteredAndSortedData.length}
        </div>
        {filteredAndSortedData.length > 0 ? (
          filteredAndSortedData.map((item) => (
            <div
              key={item.id}
              className={styles.historyBox}
              onClick={() => {
                setSelectedImage(item.overlay_image_base64);
                setSelectedAnalysis(item.analysis_result || "");
                setSelectedItem(item);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelectedImage(item.overlay_image_base64);
                  setSelectedAnalysis(item.analysis_result || "");
                  setSelectedItem(item);
                }
              }}
              aria-label={`Zobrazit heatmapu ${item.name}`}
            >
              <div className={styles.imageBox}>
                {(() => {
                  const { scoreVal, scoreMax } = parseScore(
                    extractPayload(item.analysis_result)
                  );
                  if (
                    typeof scoreVal === "number" &&
                    typeof scoreMax === "number"
                  ) {
                    return (
                      <div
                        className={styles.scoreBadgeOverlay}
                        aria-label="Skóre banneru"
                      >
                        <span className={styles.scoreLabel}>Skóre:</span>
                        <span className={styles.scoreValue}>{scoreVal}</span>
                        <span className={styles.scoreSlash}>/</span>
                        <span className={styles.scoreTotal}>{scoreMax}</span>
                      </div>
                    );
                  }
                  return null;
                })()}
                <img
                  src={`data:image/png;base64,${item.overlay_image_base64}`}
                  alt={`Heatmapa: ${item.name}`}
                  className={styles.historyImage}
                />
              </div>
              <div className={styles.infoBox}>
                <p className={styles.historyName} title={item.name}>
                  {item.name}
                </p>
                <div className={styles.infoRow}>
                  <p className={styles.historyDate}>
                    {formatDateLocal(item.created_at)}
                  </p>
                  {isValidAnalysisPayload(
                    extractPayload(item.analysis_result)
                  ) ? (
                    <span
                      className={styles.aiChip}
                      title="AI analýza k dispozici"
                      aria-label="AI analýza k dispozici"
                    >
                      <BsStars className={styles.aiChipIcon} />
                      {/* <span className={styles.aiChipText}>AI</span> */}
                    </span>
                  ) : (
                    <span />
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className={styles.placeholderFullWidth}>
            {historyData.length === 0
              ? "Nemáte žádné historické záznamy."
              : "Vašemu výběru neodpovídají žádné výsledky."}
          </div>
        )}
      </div>

      {selectedImage && (
        <HeatmapPreviewModal
          onClose={handleCloseModal}
          imageSrc={`data:image/png;base64,${selectedImage}`}
          title={selectedItem?.name}
          analysisNode={renderAnalysisContent(extractPayload(selectedAnalysis))}
          bgOpacity={1}
          score={(() => {
            const { scoreVal, scoreMax } = parseScore(
              extractPayload(selectedAnalysis)
            );
            return typeof scoreVal === "number" && typeof scoreMax === "number"
              ? { val: scoreVal, max: scoreMax }
              : null;
          })()}
          primaryAction={(() => {
            const parsed = extractPayload(selectedAnalysis);
            const hasValid = isValidAnalysisPayload(parsed);
            return hasValid
              ? null
              : {
                  icon: <ReactSVG src={wandIcon} />,
                  ariaLabel: "AI analýza",
                  title: "AI analýza",
                  onClick: () => setIsMagicOpen(true),
                  disabled: isMagicSubmitting,
                };
          })()}
        />
      )}

      {isMagicOpen && selectedItem && !selectedAnalysis && (
        <AIPurposeModal
          selectedType={magicType}
          onChangeType={setMagicType}
          onClose={() => setIsMagicOpen(false)}
          isSubmitting={isMagicSubmitting}
          onConfirm={async () => {
            if (!selectedItem || isMagicSubmitting) return;
            // In history, backend expects the record id; fall back to heatmap_id or image_id if present
            const analysisId =
              selectedItem.id ||
              selectedItem.heatmap_id ||
              selectedItem.image_id;
            if (!analysisId) {
              setIsMagicOpen(false);
              return;
            }
            // Credit pre-check: require at least 1 credit before triggering AI analysis
            const currentCredits = Math.max(
              0,
              Number(userData?.available_credits ?? 0)
            );
            if (currentCredits <= 0) {
              setIsMagicOpen(false);
              if (typeof setShowModalCredit === "function")
                setShowModalCredit(true);
              return;
            }
            setIsMagicSubmitting(true);
            try {
              const { ok, payload } = await requestAnalysisWithRetry(
                analysisId,
                PROMPT_MAP[magicType] || "",
                10
              );
              // On failure, provide a structured error payload so the UI can render a friendly message and allow retry
              const raw = ok
                ? payload ?? ""
                : {
                    _error: true,
                    message: "Při pokusu o AI analýzu došlo k chybě.",
                    email: "info@permoniq.com",
                  };
              // Show the result right away in the preview modal
              setSelectedAnalysis(raw);
              // Also reflect the result on the selected item so badges/state align
              setSelectedItem((prev) =>
                prev ? { ...prev, analysis_result: raw } : prev
              );
              // Deduct 1 credit locally after a completed request
              setUserData((prev) => {
                const prevCredits = Math.max(
                  0,
                  Number(prev?.available_credits ?? currentCredits)
                );
                const newCredits = Math.max(0, prevCredits - 1);
                return { ...prev, available_credits: newCredits };
              });
              setIsMagicOpen(false);
              if (!ok) {
                console.warn(
                  "AI analysis (history) didn't return valid JSON within retry limit."
                );
              }
              // Notify parent only on successful, valid analysis to avoid reloading entire history on error
              try {
                const valid = ok && isValidAnalysisPayload(payload);
                if (valid && typeof onAiAnalysisCompleted === "function") {
                  onAiAnalysisCompleted();
                }
              } catch (e) {
                // no-op
              }
            } finally {
              setIsMagicSubmitting(false);
            }
          }}
        />
      )}

      {isMagicSubmitting && <ModalLoading message="Probíhá analýza" />}
    </>
  );
};

export default HistoryView;
